import React, { useEffect, useState } from 'react';
import { Loader2, ShieldAlert, Check, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminNotificationsPanel = ({ csrfToken, onLogout, fmtDate, onGoToCoaching, onChanged }) => {
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const fetchWithCsrf = (url, opts = {}) => fetch(url, {
    ...opts, credentials: 'include',
    headers: { ...(opts.headers || {}), 'X-CSRF-Token': csrfToken },
  });

  const load = async () => {
    try {
      const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/coach-notifications`);
      if (res.status === 401) { onLogout(); return; }
      const data = await res.json();
      setItems(data.notifications || []);
    } catch { setItems([]); }
  };

  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    setBusyId(id);
    try {
      await fetchWithCsrf(`${BACKEND_URL}/api/admin/coach-notifications/${id}/read`, { method: 'POST' });
      setItems((its) => its.map((n) => (n.id === id ? { ...n, read: true } : n)));
      onChanged && onChanged();
    } finally { setBusyId(null); }
  };

  if (items === null) return <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;

  if (items.length === 0) {
    return (
      <div className="bg-zinc-900 border border-white/10 rounded-2xl p-10 text-center text-zinc-500" data-testid="admin-notifications-empty">
        Aucune alerte pour le moment.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="admin-notifications-panel">
      {items.map((n) => (
        <div key={n.id} className={`rounded-2xl border p-4 flex items-start justify-between gap-4 ${n.read ? 'bg-zinc-900/40 border-white/5' : 'bg-amber-500/[0.05] border-amber-500/25'}`} data-testid={`notification-${n.id}`}>
          <div className="flex items-start gap-3">
            <ShieldAlert className={`w-5 h-5 mt-0.5 shrink-0 ${n.read ? 'text-zinc-600' : 'text-amber-400'}`} />
            <div>
              <p className="text-sm text-white font-semibold">
                Objectif calorique bas — {n.name || n.email}
              </p>
              <p className="text-xs text-zinc-400 mt-1">
                {n.data?.calories} kcal défini (seuil recommandé : {n.data?.floor} kcal) · {fmtDate ? fmtDate(n.created_at) : n.created_at}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => onGoToCoaching && onGoToCoaching(n.email)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-zinc-300 border border-white/10 hover:text-white hover:border-primary/50 transition-colors"
              data-testid={`notification-goto-${n.id}`}>
              <ExternalLink className="w-3.5 h-3.5" /> Voir le client
            </button>
            {!n.read && (
              <button onClick={() => markRead(n.id)} disabled={busyId === n.id}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-emerald-300 border border-emerald-500/25 hover:bg-emerald-500/10 transition-colors disabled:opacity-50"
                data-testid={`notification-read-${n.id}`}>
                {busyId === n.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Marquer lu
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AdminNotificationsPanel;
